import React, { useState, useEffect, useRef } from 'react';
import { login, checkSignIn, openAccount, lastService, goToService, dispatcherForgot, registerUser, requestPasswordReset, saveFaceIdSession, hasFaceIdSession, restoreFaceIdSession, clearFaceIdSession, faceIdEmail } from '../traccar';
import { biometryAvailable, unlockWithBiometrics } from '../biometric';
import { TagAlongWordmark } from '../TagAlongLogo';
import { useLang, translate, setLang } from '../i18n';
import { PRODUCT } from '../product';
import { PRODUCTS, productById, addOrder } from '../ordersStore';
import { addCustomer } from '../customerStore';
import LoginBackground from './LoginBackground';
import ServiceTabs from './ServiceTabs';

const inp = { padding: '12px 14px', border: '1px solid #cbd5e1', borderRadius: '10px', fontSize: '15px', width: '100%', boxSizing: 'border-box', background: '#fff' };
const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Customer auth for TagAlong: sign in, create account, or reset password.
// All run against the Traccar server (session / registration / password reset).
//
// `variant` picks the branding: 'consumer' (default) is the TagAlong home login;
// 'pro' is the company-catered TagAlong Pro login (own wordmark, business copy,
// no consumer-only entry links). The auth flow itself is identical.
export default function LoginPage({ onLoggedIn, variant = 'consumer' }) {
  // Pro either because we were rendered as the Pro variant, OR because the build/
  // URL resolved to the Pro product (?pro, /pro, pro.* domain). This second path
  // means the company login works from LoginPage alone — no App.js routing needed.
  const isPro = variant === 'pro' || PRODUCT.key === 'pro';
  // The Pro video is mostly deep blue, so grey labels and blue links wash out.
  // On Pro: grey text → near-black, blue links → bold orange.
  const labelColor = isPro ? '#0b1220' : '#64748b';
  const linkColor = isPro ? '#f8703c' : '#1d4ed8';
  const [lang] = useLang();
  const t = (k) => translate(lang, k);
  const es = lang === 'es';
  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot'
  const [choices, setChoices] = useState(null); // more than one account on this email → a tab per service
  const [companyName, setCompanyName] = useState(''); // Pro only: the business name
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(() => { try { return localStorage.getItem('ta-remember-email') || ''; } catch { return ''; } });
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true); // keep me signed in / remember email
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [agree, setAgree] = useState(false);
  // Face ID login (native): enroll on this login, and offer quick sign-in
  const [faceIdEnroll, setFaceIdEnroll] = useState(true);
  const [bioAvail, setBioAvail] = useState(false);
  const [hasFace, setHasFace] = useState(hasFaceIdSession());
  useEffect(() => { biometryAvailable().then(setBioAvail).catch(() => {}); }, []);
  const faceIdLogin = async () => {
    reset(); setBusy(true);
    const ok2 = await unlockWithBiometrics(es ? 'Inicia sesión en TagAlong' : 'Sign in to TagAlong');
    if (!ok2) { setBusy(false); setErr(es ? 'No se pudo verificar con Face ID.' : "Couldn't verify with Face ID."); return; }
    const me = restoreFaceIdSession();
    setBusy(false);
    if (me) onLoggedIn && onLoggedIn(me);
    else { setHasFace(false); setErr(es ? 'La sesión de Face ID expiró. Inicia sesión con tu contraseña.' : 'Your Face ID session expired — sign in with your password.'); }
  };
  // Auto-prompt Face ID the moment the login screen opens (like a banking app):
  // if a saved session + biometrics are available, fire it once with no tap.
  const autoTriedRef = useRef(false);
  useEffect(() => {
    if (autoTriedRef.current) return;
    if (mode === 'login' && bioAvail && hasFace && !busy) {
      autoTriedRef.current = true;
      faceIdLogin();
    }
  }, [bioAvail, hasFace, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  // online checkout (signup track): products + shipping address
  const [cart, setCart] = useState({}); // { productId: qty }
  const [addr, setAddr] = useState({ address: '', city: '', state: '', zip: '' });
  const setQty = (id, q) => setCart((c) => { const n = { ...c, [id]: Math.max(0, q) }; if (!n[id]) delete n[id]; return n; });
  const cartItems = Object.entries(cart).map(([productId, qty]) => ({ productId, qty }));
  const cartTotal = cartItems.reduce((s, it) => { const p = productById(it.productId); return s + (p ? p.price * it.qty : 0); }, 0);

  const reset = () => { setErr(''); setOk(''); };

  // open one account's service: the TagAlong app here, the console / portals on their own pages
  const openService = (acc) => {
    const user = openAccount(acc);
    if (user.kind === 'dispatcher') { onLoggedIn && onLoggedIn({ ...user, role: 'dispatcher' }); return; }
    if (user.kind !== 'owner') { goToService(user); return; }
    // stash a Face ID session so they can log back in with a glance next time
    try { if (faceIdEnroll && bioAvail) { saveFaceIdSession(); setHasFace(true); } else if (!faceIdEnroll) { clearFaceIdSession(); setHasFace(false); } } catch { /* ignore */ }
    onLoggedIn && onLoggedIn(user);
  };

  const submit = async (e) => {
    e && e.preventDefault();
    reset(); setBusy(true);
    // remember (or forget) the email so it's pre-filled next time
    try {
      if (remember && email.trim()) localStorage.setItem('ta-remember-email', email.trim());
      else localStorage.removeItem('ta-remember-email');
    } catch { /* ignore */ }
    try {
      if (mode === 'login') {
        // one sign-in for every account — it lands on the service the account is for; more than one → tabs
        const j = await checkSignIn(email.trim(), password);
        if (j.accounts && j.accounts.length > 1) { setChoices(j.accounts); return; }
        openService(j);
      } else if (mode === 'signup') {
        if (password.length < 6) throw new Error(es ? 'La contraseña debe tener al menos 6 caracteres.' : 'Password must be at least 6 characters.');
        // Products are OPTIONAL — you can create the account now and buy a device,
        // or add one with a share code, from inside the app afterward.
        if (!agree) throw new Error(es ? 'Debes aceptar los Términos, la Privacidad y el Aviso de Rastreo para continuar.' : 'Please agree to the Terms, Privacy Policy, and Tracking Consent to continue.');
        const contactName = `${firstName.trim()} ${lastName.trim()}`.trim();
        // For Pro, the account is the company; the contact person rides alongside.
        const fullName = isPro && companyName.trim()
          ? `${companyName.trim()}${contactName ? ` (${contactName})` : ''}`
          : contactName;
        // 1) create their Traccar login
        await registerUser({ name: fullName, email: email.trim(), password, phone: phone.trim() });
        // 2) create their CRM record (account number, contact, ship-to). The plan
        //    (if any) comes from a membership product in the cart.
        const planItem = cartItems.map((it) => productById(it.productId)).find((p) => p && p.plan);
        const cust = addCustomer({
          name: fullName, email: email.trim(), phone: phone.trim(),
          address: addr.address, city: addr.city, state: addr.state, zip: addr.zip,
          status: 'Customer', services: { tagalong: true, dispatch: false },
          plan: (planItem && planItem.plan) || 'none',
        });
        // 3) only log an order if they actually chose something to buy. Otherwise
        //    the account is created empty and they buy / add a device in-app later.
        if (cartItems.length > 0) {
          addOrder({
            customerId: cust.id, account: cust.account, customerName: fullName,
            email: email.trim(), phone: phone.trim(),
            address: addr.address, city: addr.city, state: addr.state, zip: addr.zip,
            items: cartItems, source: 'online', status: 'new', paid: false,
          });
        }
        const user = await login(email.trim(), password); // auto sign-in after signup
        onLoggedIn && onLoggedIn(user);
      } else { // forgot
        dispatcherForgot(email.trim()).catch(() => {});   // a dispatcher account with this email gets its own reset link
        await requestPasswordReset(email.trim());
        setOk(es ? 'Si el correo existe, enviamos un enlace para restablecer la contraseña. Revisa tu bandeja.' : "If that email exists, we sent a reset link. Check your inbox.");
      }
    } catch (ex) {
      setErr(es && /password|401|Wrong/.test(ex.message) ? 'Correo o contraseña incorrectos.' : ex.message);
    } finally { setBusy(false); }
  };

  const L = {
    title: { login: isPro ? (es ? 'Acceso de empresa' : 'Company sign-in') : (es ? 'Iniciar sesión' : 'Sign in'), signup: isPro ? (es ? 'Crear cuenta de empresa' : 'Create company account') : (es ? 'Crear cuenta' : 'Create account'), forgot: es ? 'Restablecer contraseña' : 'Reset password' }[mode],
    cta: { login: es ? 'Iniciar sesión' : 'Sign in', signup: es ? 'Crear mi cuenta' : 'Create account', forgot: es ? 'Enviar enlace' : 'Send reset link' }[mode],
    busy: { login: es ? 'Entrando…' : 'Signing in…', signup: es ? 'Creando…' : 'Creating…', forgot: es ? 'Enviando…' : 'Sending…' }[mode],
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#160a24', padding: '20px' }}>
      <LoginBackground />
      {/* gentle vignette for depth behind the centered card; keeps the guy + car bright */}
      <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 40% 60% at 50% 50%, rgba(10,6,20,0.45), rgba(10,6,20,0) 70%)', zIndex: 0 }} />
      <div style={{ position: 'relative', zIndex: 1, width: mode === 'signup' ? 'min(460px, 96vw)' : 'min(400px, 96vw)', maxHeight: '96vh', overflowY: 'auto', background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(28px) saturate(160%)', WebkitBackdropFilter: 'blur(28px) saturate(160%)', border: '1px solid rgba(255,255,255,0.45)', borderRadius: '20px', padding: '28px', boxShadow: '0 30px 70px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.6)' }}>
        <div style={{ textAlign: 'center', marginBottom: '6px' }}>
          <TagAlongWordmark size={38} color="#12324a" pin="#f8703c" />
        </div>
        <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 800, color: '#f8703c', fontStyle: 'italic', marginBottom: isPro ? '4px' : '18px', textShadow: isPro ? '0 1px 2px rgba(0,0,0,0.25)' : 'none' }}>{isPro ? (es ? 'Los vehículos de tu empresa, siempre a la vista.' : 'Your company vehicles, always in view.') : t('tagline')}</div>
        {isPro && (
          <div style={{ textAlign: 'center', fontSize: '11.5px', color: labelColor, marginBottom: '18px' }}>{es ? 'Portal empresarial · Cuenta de empresa' : 'Business portal · Company account'}</div>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '18px' }}>
          {[['en', 'English'], ['es', 'Español']].map(([v, label]) => (
            <button key={v} onClick={() => setLang(v)} style={{ flex: 1, background: lang === v ? 'linear-gradient(135deg,#0066ff,#0047d0)' : '#f1f5f9', color: lang === v ? 'white' : '#475569', border: 'none', borderRadius: '10px', padding: '9px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>{label}</button>
          ))}
        </div>

        <div style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '17px', color: '#0f172a', marginBottom: '12px' }}>{L.title}</div>

        {choices ? (
          <div>
            <ServiceTabs accounts={choices} last={lastService(email)} es={es} onPick={openService} />
            <button type="button" onClick={() => setChoices(null)} style={{ width: '100%', marginTop: 10, background: 'transparent', border: 'none', color: '#0047d0', fontWeight: 700, cursor: 'pointer' }}>{es ? '‹ Volver' : '‹ Back'}</button>
          </div>
        ) : (
        <form onSubmit={submit}>
          {mode === 'signup' && (
            <>
              {isPro && (
                <>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: labelColor }}>{es ? 'Nombre de la empresa' : 'Company name'}</label>
                  <input style={{ ...inp, margin: '5px 0 12px' }} value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder={es ? 'Tu empresa, LLC' : 'Your Company, LLC'} />
                  <div style={{ fontSize: '11px', color: '#64748b', margin: '-6px 0 12px' }}>👤 {es ? 'La persona de contacto de la cuenta:' : 'Account contact person:'}</div>
                </>
              )}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: labelColor }}>{es ? 'Nombre' : 'First name'}</label>
                  <input style={{ ...inp, margin: '5px 0 12px' }} value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder={es ? 'Nombre' : 'First'} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', fontWeight: 700, color: labelColor }}>{es ? 'Apellido' : 'Last name'}</label>
                  <input style={{ ...inp, margin: '5px 0 12px' }} value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder={es ? 'Apellido' : 'Last'} />
                </div>
              </div>
              <label style={{ fontSize: '12px', fontWeight: 700, color: labelColor }}>{es ? 'Teléfono' : 'Phone number'}</label>
              <input style={{ ...inp, margin: '5px 0 4px' }} type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={es ? '305 555 5555' : '305 555 5555'} />
              <div style={{ fontSize: '11px', color: '#64748b', margin: '0 0 12px' }}>📱 {es ? 'Este es el número principal donde recibirás las alertas. Puedes cambiarlo luego en tu perfil.' : "This is the main number that will receive your alerts. You can change it later in your profile."}</div>
            </>
          )}
          <label style={{ fontSize: '12px', fontWeight: 700, color: labelColor }}>{es ? 'Correo electrónico' : 'Email'}</label>
          <input style={{ ...inp, margin: '5px 0 12px' }} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={es ? 'tu@correo.com' : 'you@email.com'} />

          {mode !== 'forgot' && (
            <>
              <label style={{ fontSize: '12px', fontWeight: 700, color: labelColor }}>{es ? 'Contraseña' : 'Password'}</label>
              <div style={{ position: 'relative', margin: '5px 0 12px' }}>
                <input style={{ ...inp, paddingRight: '46px' }} type={showPwd ? 'text' : 'password'} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} title={showPwd ? (es ? 'Ocultar' : 'Hide') : (es ? 'Mostrar' : 'Show')}
                  style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '4px 8px' }}>{showPwd ? '🙈' : '👁️'}</button>
              </div>
              {mode === 'login' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: labelColor, margin: '2px 0 12px', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ width: '17px', height: '17px', accentColor: '#0066ff' }} />
                  {es ? 'Mantener sesión iniciada' : 'Keep me signed in'}
                </label>
              )}
              {mode === 'login' && bioAvail && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: labelColor, margin: '-4px 0 12px', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={faceIdEnroll} onChange={(e) => setFaceIdEnroll(e.target.checked)} style={{ width: '17px', height: '17px', accentColor: '#0066ff' }} />
                  {es ? 'Habilitar inicio con Face ID' : 'Enable Face ID login'}
                </label>
              )}
            </>
          )}

          {mode === 'signup' && (
            <>
              <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0 14px' }} />
              <div style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a', marginBottom: '2px' }}>{es ? 'Elige tus productos' : 'Choose your products'} <span style={{ fontWeight: 600, color: '#94a3b8', fontSize: '12px' }}>· {es ? 'opcional' : 'optional'}</span></div>
              <div style={{ fontSize: '11.5px', color: '#64748b', marginBottom: '10px' }}>{es ? 'Puedes crear tu cuenta ahora y comprar o agregar un dispositivo con un código después.' : 'You can create your account now and buy — or add a device with a share code — later.'}</div>
              {PRODUCTS.map((p) => {
                const qty = cart[p.id] || 0;
                return (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', border: `1px solid ${qty ? '#93c5fd' : '#e2e8f0'}`, background: qty ? '#eff6ff' : '#fff', borderRadius: '10px', padding: '10px', marginBottom: '8px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{p.name}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{p.blurb}</div>
                      <div style={{ fontSize: '12px', fontWeight: 800, color: '#0066ff' }}>{money(p.price)}{p.kind === 'plan' ? (p.plan === 'talk' ? (es ? '/mes' : '/mo') : (es ? '/año' : '/yr')) : ''}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button type="button" onClick={() => setQty(p.id, qty - 1)} style={qtyBtn}>−</button>
                      <span style={{ minWidth: '18px', textAlign: 'center', fontWeight: 800 }}>{qty}</span>
                      <button type="button" onClick={() => setQty(p.id, qty + 1)} style={qtyBtn}>+</button>
                    </div>
                  </div>
                );
              })}

              <div style={{ fontWeight: 800, fontSize: '14px', color: '#0f172a', margin: '14px 0 8px' }}>{es ? 'Dirección de envío' : 'Shipping address'}</div>
              <input style={{ ...inp, margin: '0 0 8px' }} value={addr.address} onChange={(e) => setAddr((a) => ({ ...a, address: e.target.value }))} placeholder={es ? 'Dirección' : 'Street address'} />
              <div style={{ display: 'flex', gap: '8px' }}>
                <input style={{ ...inp, margin: '0 0 8px', flex: 2 }} value={addr.city} onChange={(e) => setAddr((a) => ({ ...a, city: e.target.value }))} placeholder={es ? 'Ciudad' : 'City'} />
                <input style={{ ...inp, margin: '0 0 8px', flex: 1 }} value={addr.state} onChange={(e) => setAddr((a) => ({ ...a, state: e.target.value }))} placeholder={es ? 'Estado' : 'State'} />
                <input style={{ ...inp, margin: '0 0 8px', flex: 1 }} value={addr.zip} onChange={(e) => setAddr((a) => ({ ...a, zip: e.target.value }))} placeholder={es ? 'C.P.' : 'ZIP'} />
              </div>

              {cartTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: '16px', color: '#0f172a', margin: '6px 0 12px' }}>
                  <span>{es ? 'Total' : 'Total'}</span><span>{money(cartTotal)}</span>
                </div>
              )}
            </>
          )}

          {err && <div style={{ fontSize: '13px', color: '#dc2626', marginBottom: '10px', fontWeight: 600 }}>{err}</div>}
          {ok && <div style={{ fontSize: '13px', color: '#15803d', marginBottom: '10px', fontWeight: 600 }}>{ok}</div>}

          {mode === 'signup' && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', fontSize: '12px', color: '#334155', margin: '4px 0 12px', cursor: 'pointer', lineHeight: 1.45 }}>
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: '2px', width: '16px', height: '16px', flexShrink: 0 }} />
              <span>
                {es ? 'He leído y acepto los ' : 'I have read and agree to the '}
                <a href="/terms" target="_blank" rel="noreferrer" style={{ color: linkColor, fontWeight: 800 }}>{es ? 'Términos' : 'Terms'}</a>{', '}
                <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: linkColor, fontWeight: 800 }}>{es ? 'Privacidad' : 'Privacy Policy'}</a>{es ? ' y el ' : ', and '}
                <a href="/consent" target="_blank" rel="noreferrer" style={{ color: linkColor, fontWeight: 800 }}>{es ? 'Aviso de Rastreo' : 'Tracking Consent'}</a>
                {es ? ', y confirmo que tengo derecho a rastrear los vehículos de mi cuenta.' : ', and I confirm I have the right to track the vehicles on my account.'}
              </span>
            </label>
          )}
          <button type="submit" disabled={busy || !email || (mode !== 'forgot' && !password) || (mode === 'signup' && (!firstName || !phone || !agree))}
            style={{ width: '100%', background: busy ? '#94a3b8' : 'linear-gradient(135deg,#0066ff,#0047d0)', color: 'white', fontWeight: 800, fontSize: '15px', border: 'none', borderRadius: '10px', padding: '13px', cursor: busy ? 'default' : 'pointer' }}>
            {busy ? L.busy : (mode === 'signup' && cartTotal > 0 ? `${es ? 'Pagar' : 'Pay'} ${money(cartTotal)} · ${es ? 'crear cuenta' : 'create account'}` : L.cta)}
          </button>
          {mode === 'signup' && (
            <div style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'center', marginTop: '8px' }}>🔒 {es ? 'Pago seguro procesado por Stripe.' : 'Secure payment processed by Stripe.'}</div>
          )}
        </form>
        )}

        {/* mode switches */}
        <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '13px', color: labelColor }}>
          {mode === 'login' && (
            <>
              <button onClick={() => { setMode('forgot'); reset(); }} style={linkBtn}>{es ? '¿Olvidaste tu contraseña?' : 'Forgot password?'}</button>
              <div style={{ marginTop: '8px' }}>
                {es ? '¿Nuevo cliente? ' : 'New customer? '}
                <button onClick={() => { setMode('signup'); reset(); }} style={linkBtnBold}>{es ? 'Crear cuenta' : 'Create account'}</button>
              </div>
              {/* OC drivers: share location for a load with the code dispatch texted */}
              <div style={{ marginTop: '8px' }}>
                {es ? '¿Lleva una carga? ' : 'Driving a load? '}
                <button onClick={() => { window.location.assign('/t'); }} style={linkBtnBold}>{es ? 'Código de carga' : 'Enter load code'}</button>
              </div>
              {/* company drivers: their own profile, signed in until they sign out */}
              <div style={{ marginTop: '8px' }}>
                {es ? '¿Chofer de Florida Beauty? ' : 'Florida Beauty driver? '}
                <button onClick={() => { window.location.assign('/driver'); }} style={linkBtnBold}>{es ? 'Entrar como chofer' : 'Driver sign-in'}</button>
              </div>
            </>
          )}
          {mode === 'signup' && (
            <>{es ? '¿Ya tienes cuenta? ' : 'Already have an account? '}<button onClick={() => { setMode('login'); reset(); }} style={linkBtnBold}>{es ? 'Iniciar sesión' : 'Sign in'}</button></>
          )}
          {mode === 'forgot' && (
            <button onClick={() => { setMode('login'); reset(); }} style={linkBtnBold}>{es ? '‹ Volver a iniciar sesión' : '‹ Back to sign in'}</button>
          )}
          <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #eef2f7', fontSize: '11px', color: '#94a3b8' }}>
            {es ? 'Un solo ingreso para todo: clientes, flotas, corredores, despacho y administradores — te lleva a tu servicio.' : 'One sign-in for everything — customers, fleets, brokers, dispatch and admins. It takes you to your service.'}
          </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap', fontSize: '11px' }}>
            <a href="/privacy" style={{ color: '#64748b', textDecoration: 'none' }}>{es ? 'Privacidad' : 'Privacy'}</a>
            <a href="/terms" style={{ color: '#64748b', textDecoration: 'none' }}>{es ? 'Términos' : 'Terms'}</a>
            <a href="/consent" style={{ color: '#64748b', textDecoration: 'none' }}>{es ? 'Consentimiento' : 'Tracking Consent'}</a>
          </div>
        </div>

        <p style={{ fontSize: '11px', color: '#94a3b8', textAlign: 'center', marginTop: '16px' }}>
          {mode === 'signup'
            ? (es ? 'Crea tu cuenta con el correo asociado a tu dispositivo. Un agente vinculará tu auto a tu cuenta.' : 'Create your account with the email tied to your device. An agent links your car to your account.')
            : (es ? 'Usa el correo y la contraseña de tu dispositivo.' : 'Use the email and password for your device.')}
        </p>

        {/* company contact — tap to call on a phone */}
        <p style={{ fontSize: '12.5px', color: labelColor, textAlign: 'center', marginTop: '10px' }}>
          {es ? '¿Necesitas ayuda?' : 'Need help?'}{' '}
          <a href="tel:+13055040711" style={{ color: '#1d4ed8', fontWeight: 800, textDecoration: 'none', whiteSpace: 'nowrap' }}>
            📞 (305) 504-0711
          </a>
        </p>
      </div>
    </div>
  );
}

const linkBtn = { background: 'transparent', border: 'none', color: PRODUCT.key === 'pro' ? '#f8703c' : '#0066ff', fontWeight: PRODUCT.key === 'pro' ? 800 : 600, fontSize: '13px', cursor: 'pointer', padding: 0 };
const linkBtnBold = { ...linkBtn, fontWeight: 800 };
const qtyBtn = { width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', fontSize: '16px', fontWeight: 800, color: '#334155', cursor: 'pointer', lineHeight: 1 };
